import { RSAA } from 'redux-api-middleware';
import { SHOPPER_URLS_REQUEST, SHOPPER_URLS_SUCCESS, SHOPPER_URLS_FAILURE } from '../actionTypes/shopperUrlsActionTypes';
import { dealerSelectors, selectedOfferSelectors } from '@makemydeal/dr-shared-store';
import { MANAGER_VIEW_APP_PREFIX, apiConfigHelper, locHrefUtil } from '@makemydeal/dr-shared-ui-utils';

const ENDPOINT_PATH = 'deal/links';

/**
 * Parameters for generating shopper URLs
 */
export interface GenerateShopperUrlsParams {
    dealExchangeDealId: string;
    dealer: {
        dealerId: number;
        enableNonAMDCustomerExperience?: boolean;
    };
}

/**
 * Action creator to generate shopper URLs after deal initialization.
 * This defers the short.io API call from the saveOffer lambda to improve performance.
 *
 * Triggered by middleware after INIT_PENCIL_SUCCESS when enableDeferredShopperUrlGeneration
 * feature toggle is enabled.
 *
 * @param params - Deal and dealer information needed to generate URLs
 * @returns Redux API Middleware (RSAA) action
 */
export const generateShopperUrls = (params: GenerateShopperUrlsParams) => {
    const queryParams = new URLSearchParams({
        dealExchangeDealId: params.dealExchangeDealId,
        dealerId: params.dealer.dealerId.toString(),
        ...(params.dealer.enableNonAMDCustomerExperience !== undefined && {
            enableNonAMDCustomerExperience: params.dealer.enableNonAMDCustomerExperience.toString()
        })
    });

    const locHref = locHrefUtil.getLocHref();
    const endpoint = apiConfigHelper.buildBffApiUrl(
        MANAGER_VIEW_APP_PREFIX,
        locHref,
        `${ENDPOINT_PATH}?${queryParams.toString()}`
    ).url;

    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            types: [
                SHOPPER_URLS_REQUEST,
                SHOPPER_URLS_SUCCESS,
                {
                    type: SHOPPER_URLS_FAILURE,
                    meta: {
                        dealExchangeId: params.dealExchangeDealId
                    }
                }
            ]
        }
    };
};

/**
 * Thunk to generate shopper URLs based on current deal state.
 *
 * Extracts required deal/dealer data from state and dispatches generateShopperUrls action
 * if all required data exists.
 *
 * @returns Thunk action that conditionally dispatches URL generation
 */
export const generateShopperUrlsFromState = () => (dispatch: any, getState: () => any) => {
    const state = getState();
    const dealExchangeDealId = selectedOfferSelectors.getSelectedOfferDealXgId(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const enableNonAMDCustomerExperience = dealerSelectors.isCustomerViewExperience(state);

    if (dealExchangeDealId && dealerId) {
        return dispatch(
            generateShopperUrls({
                dealExchangeDealId,
                dealer: {
                    dealerId,
                    enableNonAMDCustomerExperience
                }
            })
        );
    }

    return undefined;
};
